/**
 * CommandPalette.jsx — Quick navigation + actions (Cmd+K / Ctrl+K)
 *
 * Features:
 * - Page navigation commands
 * - Action commands (create job, add contact, etc.)
 * - Global entity search (jobs, contacts, invoices by name)
 * - Opens with Cmd+K (Mac) or Ctrl+K (Windows/Linux)
 * - Closes with Escape or clicking outside
 * - Filters as user types
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, Box, InputBase, Typography, List, ListItemButton, ListItemIcon, ListItemText, Divider, Chip } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useJobs } from '../../hooks/useJobs';
import { useContacts } from '../../hooks/useContacts';
import { useInvoices } from '../../hooks/useInvoices';
import { useWikiArticles } from '../../hooks/useWiki';
import { navigateToJob, navigateToContact, navigateToInvoice } from '../../lib/crossNav';
import { useRBAC } from '../../hooks/useRBAC';
import { useSettings } from '../../hooks/useSettings';
import { useAuth } from '../../hooks/useAuth';

const PAGE_COMMANDS = [
  { label: 'Home',          path: '/',          keywords: 'home dashboard overview', type: 'page' },
  { label: 'Jobs',          path: '/jobs',      keywords: 'jobs pipeline projects work', type: 'page' },
  { label: 'People / CRM',  path: '/people',    keywords: 'people contacts crm clients crew vendors', type: 'page' },
  { label: 'Calendar',      path: '/calendar',  keywords: 'calendar schedule dates shoots', type: 'page' },
  { label: 'Quotes',        path: '/quotes',    keywords: 'quotes calculator estimate pricing split revenue pay', type: 'page' },
  { label: 'Invoices',      path: '/invoices',  keywords: 'invoices billing payments money', type: 'page' },
  { label: 'Payroll',       path: '/payroll',   keywords: 'payroll pay periods time hours crew expenses costs spending recurring reports profit loss', type: 'page' },
  { label: 'How To',        path: '/howto',     keywords: 'howto how to wiki knowledge library tutorials learn videos inspiration guides training', type: 'page' },
  { label: 'Legal',         path: '/legal',     keywords: 'legal contracts documents agreements', type: 'page' },
  { label: 'Settings',      path: '/settings',  keywords: 'settings config company preferences activity log actions audit history team members roles permissions roster', type: 'page' },
];

// The rest of the sidebar's screens. Found by search only (the empty palette still
// lists just the pages above), each behind the same gate as its route, so a screen
// a Focus set hides from the sidebar is still one Ctrl+K away.
const MORE_PAGE_COMMANDS = [
  { label: 'To Do',       path: '/todos',      keywords: 'todo to-do tasks list', type: 'page' },
  { label: 'RotoTube',    path: '/rototube',   keywords: 'rototube media library videos vimeo', type: 'page' },
  { label: 'Carousel',    path: '/carousel',   keywords: 'carousel instagram slides composer', type: 'page' },
  { label: 'Outreach',    path: '/outreach',   keywords: 'outreach prospects prospecting agents cadence', type: 'page', feature: 'outreach', level: 'full' },
  { label: 'Open Houses', path: '/openhouse',  keywords: 'open house openhouse weekend route agents', type: 'page', tool: 'openhouse' },
  { label: 'Voice Notes', path: '/voicenotes', keywords: 'voice notes voicenotes plans', type: 'page', tool: 'voicenotes' },
  { label: 'Links',       path: '/links',      keywords: 'links shortener tracking', type: 'page', tool: 'links' },
  { label: 'Vehicle',     path: '/vehicle',    keywords: 'vehicle drives mileage tesla car', type: 'page', tool: 'vehicle' },
  { label: 'Projects',    path: '/projects',   keywords: 'projects staycation production', type: 'page', tool: 'projects' },
  { label: 'Calculator',  path: '/calculator', keywords: 'calculator pay split', type: 'page', feature: 'calculator', level: 'own_earnings' },
];

// `path` is the full destination, query included — each one is read by its page.
const ACTION_COMMANDS = [
  { label: 'Create New Job',      path: '/jobs?action=newJob',       keywords: 'new job create add', type: 'action' },
  { label: 'Add New Contact',     path: '/people?action=newContact', keywords: 'new contact add person client', type: 'action' },
  { label: 'Create Quote',        path: '/quotes?tab=builder',       keywords: 'new quote create estimate builder', type: 'action' },
  { label: 'Add Expense',         path: '/payroll?action=addExpense', keywords: 'expense add cost', type: 'action', feature: 'payroll', level: 'full' },
  { label: 'Add How-To Video',    path: '/howto?action=newHowto',    keywords: 'new howto video add tutorial inspiration link', type: 'action' },
  { label: 'Export Data Backup',  path: '/settings?tab=data',        keywords: 'export backup data download', type: 'action', feature: 'settings', level: 'full' },
];

const TYPE_ICONS = {
  page: '→',
  action: '⚡',
  job: '📋',
  contact: '👤',
  invoice: '💰',
  howto: '🎬',
};

const TYPE_LABELS = {
  page: 'Page',
  action: 'Action',
  job: 'Job',
  contact: 'Contact',
  invoice: 'Invoice',
  howto: 'How To',
};

export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Entity data for global search
  const { data: allJobs = [] } = useJobs();
  // Contacts are only needed once someone searches — don't pull all of them on every screen.
  const { data: allContacts = [] } = useContacts({ enabled: open });
  const { data: allInvoices = [] } = useInvoices();
  const { data: allHowTos = [] } = useWikiArticles();
  const { hasAccess, hasToolAccess } = useRBAC();
  const { data: settings } = useSettings();
  const { profile } = useAuth();

  const permitted = useCallback((cmd) => {
    if (profile?.is_external) return cmd.tool === 'projects';
    if (cmd.tool) return hasToolAccess(cmd.tool, settings);
    if (cmd.feature) return hasAccess(cmd.feature, cmd.level, settings);
    return true;
  }, [profile, hasAccess, hasToolAccess, settings]);
  const morePages = useMemo(() => MORE_PAGE_COMMANDS.filter(permitted), [permitted]);
  const actions = useMemo(() => ACTION_COMMANDS.filter(permitted), [permitted]);

  // Build entity search results
  const entityResults = useMemo(() => {
    if (!query.trim() || query.trim().length < 2) return [];

    const q = query.toLowerCase();
    const results = [];

    // Search jobs by title or client
    allJobs
      .filter((j) =>
        (j.title || '').toLowerCase().includes(q) ||
        (j.clientName || '').toLowerCase().includes(q)
      )
      .slice(0, 5)
      .forEach((j) =>
        results.push({
          label: j.title || j.clientName || '(Untitled)',
          sublabel: j.clientName,
          type: 'job',
          id: j.id,
        })
      );

    // Search contacts by name or company
    allContacts
      .filter((c) =>
        (c.name || '').toLowerCase().includes(q) ||
        (c.company || '').toLowerCase().includes(q) ||
        (c.email || '').toLowerCase().includes(q)
      )
      .slice(0, 5)
      .forEach((c) =>
        results.push({
          label: c.name || '(No Name)',
          sublabel: c.company || c.email,
          type: 'contact',
          id: c.id,
        })
      );

    // Search invoices by number or client
    allInvoices
      .filter((inv) =>
        (inv.number || '').toLowerCase().includes(q) ||
        (inv.client || '').toLowerCase().includes(q)
      )
      .slice(0, 3)
      .forEach((inv) =>
        results.push({
          label: inv.number || '(No Number)',
          sublabel: inv.client,
          type: 'invoice',
          id: inv.id,
        })
      );

    // Search how-to entries by title, category, or description
    allHowTos
      .filter((h) =>
        (h.title || '').toLowerCase().includes(q) ||
        (h.category || '').toLowerCase().includes(q) ||
        (h.description || '').toLowerCase().includes(q)
      )
      .slice(0, 4)
      .forEach((h) =>
        results.push({
          label: h.title,
          sublabel: h.category,
          type: 'howto',
          id: h.id,
        })
      );

    return results;
  }, [query, allJobs, allContacts, allInvoices, allHowTos]);

  // Build filtered command list
  const filtered = useMemo(() => {
    const all = [...PAGE_COMMANDS, ...morePages, ...actions];
    const q = query.trim().toLowerCase();

    if (!q) return PAGE_COMMANDS; // Show only pages when no query

    const commandResults = all.filter(
      (cmd) =>
        cmd.label.toLowerCase().includes(q) ||
        cmd.keywords.includes(q)
    );

    // Merge entity results at the end
    return [...commandResults, ...entityResults];
  }, [query, entityResults, morePages, actions]);

  // Reset selection when query changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  // Keyboard shortcut to open: Cmd+K / Ctrl+K
  useEffect(() => {
    function handleKeyDown(e) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(true);
        setQuery('');
      }

      if (e.key === 'Escape' && open) {
        e.preventDefault();
        setOpen(false);
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  // Focus input when opened
  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  // Navigate/execute selected command
  const selectCommand = useCallback((cmd) => {
    setOpen(false);
    setQuery('');

    // Entity navigation
    if (cmd.type === 'job') {
      navigateToJob(navigate, cmd.id);
      return;
    }
    if (cmd.type === 'contact') {
      navigateToContact(navigate, cmd.id);
      return;
    }
    if (cmd.type === 'invoice') {
      navigateToInvoice(navigate, cmd.id);
      return;
    }
    if (cmd.type === 'howto') {
      navigate(`/howto/${cmd.id}`);
      return;
    }

    // Page + action navigation
    navigate(cmd.path);
  }, [navigate]);

  // Arrow key navigation + Enter to select
  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && filtered[selectedIndex]) {
      e.preventDefault();
      selectCommand(filtered[selectedIndex]);
    }
  };

  if (!open) return null;

  return (
    <Dialog data-track-area="command-palette"
      open={open}
      onClose={() => setOpen(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { position: 'fixed', top: '15vh', m: 0, borderRadius: 2, bgcolor: 'background.paper' } }}
      slotProps={{ backdrop: { sx: { bgcolor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' } } }}
    >
      {/* Search input */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
        <InputBase
          inputRef={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Search pages, actions, jobs, contacts..."
          fullWidth
          sx={{ fontSize: '0.875rem' }}
        />
        <Chip label="ESC" size="small" variant="outlined" sx={{ fontSize: '0.6rem', height: 20 }} />
      </Box>

      {/* Command list */}
      <List dense sx={{ maxHeight: 320, overflowY: 'auto', py: 0.5 }}>
        {filtered.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 4, textAlign: 'center' }}>
            No matches found
          </Typography>
        ) : (
          filtered.map((cmd, i) => (
            <ListItemButton
              key={`${cmd.type}-${cmd.path || cmd.id}-${i}`}
              data-track={cmd.type === 'page' || cmd.type === 'action' ? `palette:${cmd.label}` : `palette.${cmd.type}`}
              onClick={() => selectCommand(cmd)}
              selected={i === selectedIndex}
              sx={{ px: 2, py: 0.75 }}
            >
              <ListItemIcon sx={{ minWidth: 28, opacity: 0.6, fontSize: '0.8rem' }}>
                {TYPE_ICONS[cmd.type] || '→'}
              </ListItemIcon>
              <ListItemText
                primary={cmd.label}
                secondary={cmd.sublabel || null}
                slotProps={{ primary: { variant: 'body2', noWrap: true }, secondary: { variant: 'caption' } }}
              />
              <Typography variant="caption" color="text.disabled" sx={{ flexShrink: 0, ml: 1 }}>
                {TYPE_LABELS[cmd.type] || ''}
              </Typography>
              {i === selectedIndex && (
                <Typography variant="caption" color="text.disabled" sx={{ ml: 1, flexShrink: 0 }}>Enter</Typography>
              )}
            </ListItemButton>
          ))
        )}
      </List>

      {/* Hint */}
      <Box sx={{ px: 2, py: 1, borderTop: 1, borderColor: 'divider', display: 'flex', gap: 3 }}>
        <Typography variant="caption" color="text.disabled">↑↓ Navigate</Typography>
        <Typography variant="caption" color="text.disabled">Enter Select</Typography>
        <Typography variant="caption" color="text.disabled">Type 2+ chars to search entities</Typography>
      </Box>
    </Dialog>
  );
}
